/**
 * B2 · CLASSROOM CHAT — Drizzle query builders + query keys.
 *
 * Everything read here arrives via `my_cohort_chat` (student) or
 * `my_cohort_chat_teacher` (whoever moderates). The client does NOT re-filter
 * by `archived_at` or `hidden_at` "to be safe": those filters are access
 * control and live in the sync rules. Filtering them again here would hide
 * a stream failure in the UI right when it needs to be seen.
 *
 * What the client DOES filter is `deleted_at` (the author's own soft-delete),
 * same as in the rest of the repo.
 */

import {
  and,
  asc,
  cohort_chat_message,
  cohort_chat_reaction,
  cohort_chat_read,
  desc,
  eq,
  isNotNull,
  isNull,
  type PowerSyncDrizzleDB,
} from "@space/powersync";

export const classroomChatQueryKeys = {
  all: ["academy-classroom-chat"] as const,
  messages: (cohortId: string | undefined) =>
    ["academy-classroom-chat", "messages", cohortId] as const,
  pinned: (cohortId: string | undefined) => ["academy-classroom-chat", "pinned", cohortId] as const,
  read: (cohortId: string | undefined, userId: string | undefined) =>
    ["academy-classroom-chat", "read", cohortId, userId] as const,
  reactions: (cohortId: string | undefined) =>
    ["academy-classroom-chat", "reactions", cohortId] as const,
};

/** A cohort's conversation, in chronological order (the live window). */
export function getCohortChatMessagesQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(cohort_chat_message)
    .where(and(eq(cohort_chat_message.cohort_id, cohortId), isNull(cohort_chat_message.deleted_at)))
    .orderBy(asc(cohort_chat_message.created_at));
}

/** A cohort's PINNED messages (the ones that don't expire with the window). */
export function getPinnedChatMessagesQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db
    .select()
    .from(cohort_chat_message)
    .where(
      and(
        eq(cohort_chat_message.cohort_id, cohortId),
        isNull(cohort_chat_message.deleted_at),
        isNotNull(cohort_chat_message.pinned_at),
      ),
    )
    .orderBy(desc(cohort_chat_message.pinned_at));
}

/** MY read marker / mute for a classroom (others' isn't synced). */
export function getMyChatReadQuery(db: PowerSyncDrizzleDB, cohortId: string, userId: string) {
  return db
    .select()
    .from(cohort_chat_read)
    .where(and(eq(cohort_chat_read.cohort_id, cohortId), eq(cohort_chat_read.user_id, userId)))
    .limit(1);
}

/** Reactions in a cohort's live window. */
export function getCohortChatReactionsQuery(db: PowerSyncDrizzleDB, cohortId: string) {
  return db.select().from(cohort_chat_reaction).where(eq(cohort_chat_reaction.cohort_id, cohortId));
}
